/**
 * MobileMenu — Radix-based dropdown menu that satisfies Contract §4.
 *
 *   • native click-outside + ESC dismiss (Radix built-in)
 *   • focus restoration to trigger on close
 *   • max height with internal scroll — never overflows viewport
 *   • collision detection (auto-flip + shift on small viewports)
 *   • items ≥44px tall on mobile
 *
 * API (kept close to Radix vocabulary so JSX is intuitive):
 *
 *   <MobileMenu>
 *     <MobileMenu.Trigger asChild><button>Open</button></MobileMenu.Trigger>
 *     <MobileMenu.Content testid="header-menu">
 *       <MobileMenu.Item onSelect={fn}>Edit</MobileMenu.Item>
 *       <MobileMenu.Separator />
 *       <MobileMenu.Item destructive onSelect={fn}>Delete</MobileMenu.Item>
 *     </MobileMenu.Content>
 *   </MobileMenu>
 *
 * Phase 1 — Mobile Shell programme. May 14, 2026.
 */
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Z_CLASS } from "./zTiers";

export function MobileMenu({ children, open, onOpenChange, ...rest }) {
  return (
    <DropdownMenu.Root open={open} onOpenChange={onOpenChange} {...rest}>
      {children}
    </DropdownMenu.Root>
  );
}

MobileMenu.Trigger = function MobileMenuTrigger({ children, ...rest }) {
  return <DropdownMenu.Trigger {...rest}>{children}</DropdownMenu.Trigger>;
};

MobileMenu.Content = function MobileMenuContent({
  children,
  testid = "mobile-menu",
  side = "bottom",
  align = "end",
  sideOffset = 6,
  collisionPadding = 12,
  className = "",
  ...rest
}) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        data-testid={testid}
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        avoidCollisions
        className={[
          // Contract §9 — menu z-tier sits above page chrome,
          // below sheets (Z_TIERS.SHEET) and urgent modals
          // (Z_TIERS.URGENT_MODAL). Source of truth: zTiers.js.
          Z_CLASS.MENU,
          "min-w-[180px] max-w-[min(92vw,320px)]",
          "max-h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-4rem)]",
          "overflow-y-auto overscroll-contain",
          "rounded-xl border border-white/10 bg-[#0a0a0a] text-white shadow-2xl",
          "p-1",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
          className,
        ].filter(Boolean).join(" ")}
        {...rest}
      >
        {children}
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  );
};

MobileMenu.Item = function MobileMenuItem({
  children,
  onSelect,
  destructive = false,
  disabled = false,
  testid,
  className = "",
  ...rest
}) {
  return (
    <DropdownMenu.Item
      data-testid={testid}
      onSelect={onSelect}
      disabled={disabled}
      className={[
        "min-h-[44px] px-3 py-2.5 text-[13.5px] rounded-lg",
        "flex items-center gap-2.5 cursor-pointer outline-none select-none",
        "data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed",
        destructive
          ? "text-rose-300 hover:bg-rose-500/[0.08] focus:bg-rose-500/[0.08]"
          : "text-zinc-200 hover:bg-white/[0.06] focus:bg-white/[0.06]",
        className,
      ].filter(Boolean).join(" ")}
      {...rest}
    >
      {children}
    </DropdownMenu.Item>
  );
};

MobileMenu.Separator = function MobileMenuSeparator() {
  return <DropdownMenu.Separator className="my-1 h-px bg-white/[0.06]" />;
};

MobileMenu.Label = function MobileMenuLabel({ children, className = "" }) {
  return (
    <DropdownMenu.Label
      className={["px-3 pt-2 pb-1 text-[10.5px] font-bold uppercase tracking-[0.18em] text-zinc-500", className].join(" ")}
    >
      {children}
    </DropdownMenu.Label>
  );
};

export default MobileMenu;
